import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { AlertTriangle, Clock, FileText, Phone, ArrowRight, CheckCircle, XCircle, Shield } from 'lucide-react';

const noticeTypes = [
  { title: 'CP2000 / Underreported Income', description: 'The IRS believes you didn\'t report all your income. This requires a careful review and a timely response.' },
  { title: 'CP14 / Balance Due', description: 'You owe a balance on your tax return. Ignoring this can lead to penalties, interest, and collection actions.' },
  { title: 'CP501–CP504 / Collection Notices', description: 'These escalate in severity. The final notice (CP504) warns of levy action on your assets.' },
  { title: 'Letter 1058 / Final Notice of Intent to Levy', description: 'This is serious. You have appeal rights, but the window is short.' },
  { title: 'CP11 / Math Error', description: 'The IRS changed your return due to a calculation error. You may agree or disagree, but you must respond.' },
  { title: 'CP22A / Changes to Your Return', description: 'The IRS made changes that resulted in a different amount owed. Review carefully before responding.' },
];

const doNots = [
  'Ignore the notice — deadlines are real and penalties increase',
  'Assume it will go away on its own',
  'Call the IRS without understanding the notice first',
  'Respond emotionally or without reviewing the facts',
];

const whatWeDo = [
  'Review the notice and explain what it means in plain English',
  'Identify the deadline and urgency level',
  'Examine whether the IRS is correct or if there are errors',
  'Recommend clear next steps based on your specific situation',
  'Draft a response or represent you if needed',
];

export default function IRSNoticeReview() {
  useSEO({
    title: 'IRS Notice Review',
    description: 'Got a letter from the IRS? We review your notice, explain what it means, and help you respond before deadlines become costly.',
  });

  return (
    <div data-testid="irs-notice-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              IRS Notice Review
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              Got a Letter From the IRS? Don't Panic.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              IRS letters and notices can be confusing and stressful. We review your notice, explain what it means, and help you respond correctly before deadlines become costly.
            </p>
            <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="irs-hero-cta"
                className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
              >
                Get an IRS Notice Review
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Common Notices */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Common IRS Notices
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4 max-w-lg">
              What does your notice say?
            </h2>
            <p className="text-base text-slate-500 mb-12 max-w-xl">
              Here are some of the most common IRS notices and what they typically mean. Each requires a different response.
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {noticeTypes.map((notice, i) => (
              <AnimatedSection key={i} delay={i * 0.06}>
                <div className="bg-white p-8 h-full">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-md bg-brand-navy/5 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-brand-navy" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-base text-brand-navy mb-2">{notice.title}</h3>
                      <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">{notice.description}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* What NOT to do + What we do */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h2 className="font-heading font-semibold text-xl text-brand-navy">What NOT to Do</h2>
              </div>
              <div className="space-y-4">
                {doNots.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-white border border-red-100 rounded-lg">
                    <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-5 h-5 text-brand-green" />
                <h2 className="font-heading font-semibold text-xl text-brand-navy">What We Do</h2>
              </div>
              <div className="space-y-4">
                {whatWeDo.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-white border border-brand-green/20 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Urgency */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl mx-auto text-center">
            <Clock className="w-12 h-12 text-brand-green mx-auto mb-5" />
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Deadlines Matter
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-8">
              Most IRS notices have a response deadline — often 30 to 60 days. Missing the deadline can mean losing your right to dispute, increased penalties, or enforcement action. The sooner you act, the more options you have.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="irs-cta-book"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
                >
                  Get an IRS Notice Review
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="tel:8555892461">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-6 text-base rounded-md">
                  <Phone className="w-4 h-4 mr-2" /> Call 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
